/**
 * Input-parity netplay (Tier 2).
 *
 * Equalises P1 (host) and P2 (guest) perceived latency in hosted
 * netplay by buffering P1's local input for N frames before the
 * emulator sees it, where N tracks half the measured RTT. P2's
 * inputs are tagged with a target frame chosen the same way, so
 * both players' inputs land on the same emulator frame.
 *
 * The module is deliberately DOM-free so it can be unit-tested in
 * a Node environment.
 */

export const FRAME_MS = 1000 / 60;
export const JITTER_MARGIN_MS = 8;
export const MIN_DELAY = 1;
export const MAX_DELAY = 8;
export const HYSTERESIS_FRAMES = 2;
export const HEALTH_WINDOW = 60;

/**
 * Target delay (frames) from measured RTT.
 * `targetDelay = ceil((RTT/2 + jitter) / frameMs)`, clamped.
 */
export function computeTargetDelay(rttMs, jitterMs = JITTER_MARGIN_MS, frameMs = FRAME_MS) {
  const rtt = Number.isFinite(rttMs) && rttMs >= 0 ? rttMs : 0;
  const raw = Math.ceil((rtt / 2 + jitterMs) / frameMs);
  if (raw < MIN_DELAY) return MIN_DELAY;
  if (raw > MAX_DELAY) return MAX_DELAY;
  return raw;
}

/**
 * Smooth the current delay toward a target. Changes of ≥ HYSTERESIS
 * are adopted immediately; smaller deltas nudge by one frame per call
 * so we don't oscillate on noisy RTT samples.
 */
export function smoothDelay(current, target) {
  if (Math.abs(target - current) >= HYSTERESIS_FRAMES) return target;
  if (target > current) return current + 1;
  if (target < current) return current - 1;
  return current;
}

/** Single-player frame-keyed input queue with health metrics. */
export class InputQueue {
  constructor() {
    this.queue = new Map();
    this.lastApplied = 0;
    this.appliedCount = 0;
    this.onTimeCount = 0;
    this.lateCount = 0;
    this.lostCount = 0;
  }

  enqueue(frame, bitmask) {
    if (!Number.isInteger(frame)) return;
    this.queue.set(frame, bitmask);
  }

  /** Late arrival — schedule for the next drainable frame. */
  enqueueLate(currentFrame, bitmask) {
    this.lateCount++;
    this.queue.set(currentFrame + 1, bitmask);
  }

  drain(currentFrame) {
    let bitmask;
    if (this.queue.has(currentFrame)) {
      bitmask = this.queue.get(currentFrame);
      this.queue.delete(currentFrame);
      this.onTimeCount++;
    } else {
      bitmask = this.lastApplied;
      this.lostCount++;
    }
    this.lastApplied = bitmask;
    this.appliedCount++;
    if (this.appliedCount >= HEALTH_WINDOW * 4) this._shrinkHealthWindow();
    return bitmask;
  }

  _shrinkHealthWindow() {
    const ratio = this.onTimeCount / this.appliedCount;
    this.appliedCount = HEALTH_WINDOW;
    this.onTimeCount = Math.round(ratio * HEALTH_WINDOW);
    this.lateCount = 0;
    this.lostCount = 0;
  }

  gc(currentFrame, maxAge = 600) {
    const cutoff = currentFrame - maxAge;
    for (const k of this.queue.keys()) {
      if (k < cutoff) this.queue.delete(k);
    }
  }

  /** Drop all pending entries, return the most-future one (or lastApplied). */
  flush() {
    let latest = this.lastApplied;
    let latestFrame = -Infinity;
    for (const [f, b] of this.queue.entries()) {
      if (f > latestFrame) { latestFrame = f; latest = b; }
    }
    this.queue.clear();
    this.lastApplied = latest;
    return latest;
  }

  /** Fraction of frames whose input arrived on time (0..1). */
  health() {
    if (this.appliedCount === 0) return 1;
    return this.onTimeCount / this.appliedCount;
  }

  size() { return this.queue.size; }
}

/**
 * Coordinates P1 and P2 input queues against a shared host frame clock.
 *
 * Usage (jsnes host loop):
 *   coord.submitP1(bitmask);  // every frame, from input.poll()
 *   coord.tick();             // drains both queues, fires onPxApply
 *   emu.stepFrame();
 *
 * Usage (P2 input receive):
 *   coord.submitP2(bitmask, targetFrame);  // targetFrame from DataChannel
 *
 * When `enabled === false`, submitP1 / submitP2 pass through to the
 * apply callbacks immediately (current direct-apply behavior), and
 * tick() just advances the frame counter. This is the fallback the
 * user can switch to from the HUD if parity feels worse than the
 * P1-advantage default.
 */
export class InputParityCoordinator {
  constructor({ onP1Apply, onP2Apply, initialDelay = 3 } = {}) {
    this.hostFrame = 0;
    this.delayFrames = initialDelay;
    this.enabled = true;
    this.p1Queue = new InputQueue();
    this.p2Queue = new InputQueue();
    this.onP1Apply = onP1Apply || null;
    this.onP2Apply = onP2Apply || null;
    this._lastP1 = 0;
    this._lastP2 = 0;
  }

  setEnabled(enabled) {
    const next = !!enabled;
    if (this.enabled === next) return;
    this.enabled = next;
    if (!next) {
      const p1 = this.p1Queue.flush();
      const p2 = this.p2Queue.flush();
      this._applyP1(p1);
      this._applyP2(p2);
    }
  }

  setDelayTarget(target) {
    this.delayFrames = smoothDelay(this.delayFrames, target);
    return this.delayFrames;
  }

  submitP1(bitmask) {
    const mask = bitmask;
    if (!this.enabled) { this._applyP1(mask); return; }
    this.p1Queue.enqueue(this.hostFrame + this.delayFrames, mask);
  }

  submitP2(bitmask, targetFrame) {
    const mask = bitmask;
    if (!this.enabled) { this._applyP2(mask); return; }
    if (!Number.isInteger(targetFrame)) {
      this._applyP2(mask);
      return;
    }
    if (targetFrame < this.hostFrame) {
      this.p2Queue.enqueueLate(this.hostFrame, mask);
    } else {
      this.p2Queue.enqueue(targetFrame, mask);
    }
  }

  tick() {
    if (!this.enabled) { this.hostFrame++; return; }
    const p1 = this.p1Queue.drain(this.hostFrame);
    const p2 = this.p2Queue.drain(this.hostFrame);
    this._applyP1(p1);
    this._applyP2(p2);
    this.hostFrame++;
    if ((this.hostFrame & 0x3FF) === 0) {
      this.p1Queue.gc(this.hostFrame);
      this.p2Queue.gc(this.hostFrame);
    }
  }

  _applyP1(mask) {
    const prev = this._lastP1;
    this._lastP1 = mask;
    if (this.onP1Apply) this.onP1Apply(mask, prev);
  }

  _applyP2(mask) {
    const prev = this._lastP2;
    this._lastP2 = mask;
    if (this.onP2Apply) this.onP2Apply(mask, prev);
  }

  health() { return this.p2Queue.health(); }
}

/**
 * Encode a guest → host input message (8 bytes).
 *   bytes 0-3: targetFrame, uint32 little-endian
 *   bytes 4-7: bitmask,     uint32 little-endian
 *
 * N64's DPAD_LEFT/RIGHT bits (0x10000/0x20000) exceed 16 bits, so the
 * bitmask is encoded as uint32. The decoder accepts both 6-byte (legacy
 * uint16 bitmask) and 8-byte (uint32) formats for backwards compat.
 */
export function encodeInputFrame(targetFrame, bitmask) {
  const buf = new ArrayBuffer(8);
  const view = new DataView(buf);
  view.setUint32(0, (targetFrame >>> 0), true);
  view.setUint32(4, bitmask >>> 0, true);
  return buf;
}

const NO_TARGET_FRAME = 0xFFFFFFFF;

function encodeAnalogAxis(value) {
  const finite = Number.isFinite(value) ? value : 0;
  return Math.round(Math.max(-1, Math.min(1, finite)) * 127);
}

/**
 * Encode proportional analog input for hosted N64 netplay (10 bytes).
 *   bytes 0-3: targetFrame, uint32 LE (0xFFFFFFFF = no frame estimate)
 *   bytes 4-7: bitmask,     uint32 LE
 *   byte  8:   analog X,    int8 (-127..127)
 *   byte  9:   analog Y,    int8 (-127..127)
 *
 * The player slot is implicit in the host's per-guest DataChannel, so it does
 * not need to consume bytes in every input packet.
 */
export function encodeInputFrameEx(targetFrame, bitmask, analogX = 0, analogY = 0) {
  const buf = new ArrayBuffer(10);
  const view = new DataView(buf);
  view.setUint32(0, Number.isInteger(targetFrame) ? (targetFrame >>> 0) : NO_TARGET_FRAME, true);
  view.setUint32(4, bitmask >>> 0, true);
  view.setInt8(8, encodeAnalogAxis(analogX));
  view.setInt8(9, encodeAnalogAxis(analogY));
  return buf;
}

/**
 * Decode a guest → host input message. Handles the 10-byte analog format,
 * the 8-byte uint32 format, legacy 6-byte uint16 format, and bare 2/4-byte
 * bitmasks (targetFrame = null).
 */
export function decodeInputFrame(arrayBuffer) {
  if (!(arrayBuffer instanceof ArrayBuffer)) return null;
  if (arrayBuffer.byteLength === 2) {
    const bitmask = new Uint16Array(arrayBuffer)[0];
    return { targetFrame: null, bitmask };
  }
  if (arrayBuffer.byteLength === 4) {
    return { targetFrame: null, bitmask: new DataView(arrayBuffer).getUint32(0, true) };
  }
  if (arrayBuffer.byteLength === 8) {
    const view = new DataView(arrayBuffer);
    return {
      targetFrame: view.getUint32(0, true),
      bitmask: view.getUint32(4, true),
    };
  }
  if (arrayBuffer.byteLength === 10) {
    const view = new DataView(arrayBuffer);
    const targetFrame = view.getUint32(0, true);
    return {
      targetFrame: targetFrame === NO_TARGET_FRAME ? null : targetFrame,
      bitmask: view.getUint32(4, true),
      analogX: view.getInt8(8) / 127,
      analogY: view.getInt8(9) / 127,
    };
  }
  if (arrayBuffer.byteLength === 6) {
    const view = new DataView(arrayBuffer);
    return {
      targetFrame: view.getUint32(0, true),
      bitmask: view.getUint16(4, true),
    };
  }
  return null;
}
